import {
  type Faro,
  getWebInstrumentations,
  initializeFaro,
  type TraceEvent,
  type TransportItem,
  TransportItemType,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { ClockOffsetEstimator, type ClockSample, sampleServerClockFromFetch } from './clockOffset.ts';
import { tagSpanWithPlayer } from './player.ts';
import { logRequestStart } from './requestStart.ts';
import { shiftTimestampOntoServerClock, shiftTraceOntoServerClock } from './traceClock.ts';

type TimestampedEvent = Exclude<TransportItem['payload'], TraceEvent>;

let faro: Faro | undefined;

const clock = new ClockOffsetEstimator();

function ontoServerClock(item: TransportItem): TransportItem {
  const sample = clock.best();
  if (sample === undefined) return item;

  if (item.type === TransportItemType.TRACE) {
    return { ...item, payload: shiftTraceOntoServerClock(item.payload as TraceEvent, sample) };
  }

  return { ...item, payload: shiftTimestampOntoServerClock(item.payload as TimestampedEvent, sample) };
}

function onRequestStart(span: Parameters<typeof logRequestStart>[0] & Parameters<typeof tagSpanWithPlayer>[0]): void {
  tagSpanWithPlayer(span);
  logRequestStart(span);
}

function reportClockOffset(sample: ClockSample): void {
  faro?.api.pushMeasurement({ type: 'clock', values: { offset_ms: sample.offsetMs, rtt_ms: sample.rttMs } });
}

export function initFaro(): Faro | undefined {
  if (faro) return faro;

  faro = initializeFaro({
    url: `${window.location.origin}/faro/collect`,
    app: {
      name: `songstar-frontend-${window.__APP_CONFIG__?.env ?? import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE}`,
      version: window.__APP_CONFIG__?.version ?? 'dev',
    },
    beforeSend: ontoServerClock,
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          fetchInstrumentationOptions: { requestHook: onRequestStart },
          propagateTraceHeaderCorsUrls: [new RegExp(`${window.location.origin}/api/.*`)],
        },
      }),
    ],
  });

  sampleServerClockFromFetch(clock, reportClockOffset);

  return faro;
}
